/**
 * ShareLinkModal Component
 *
 * Modal for creating and managing shareable links for HMC event reports.
 * Allows admins to:
 * - Generate new share links with configurable expiration (1-30 days)
 * - Add optional labels for link identification
 * - Select specific events to include in the share link
 * - Copy generated URLs to clipboard
 * - View existing links with access stats
 * - Revoke active links
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { apiClient } from '@shared/services/apiClient';
import { buildHMCGuestUrl } from '@app/routes';
import { ConfirmationDialog } from '@shared/components/ui/ConfirmationDialog';
import type { HMCEvent } from '../hmcEventsStore';
import { isAssignedSuiteEvent } from '../utils/attendanceParser';
import { parseLocalDate } from '../utils/dateUtils';

interface ShareLink {
  id: number;
  share_token: string;
  upload_id: string;
  expires_at: string;
  is_revoked: boolean;
  access_count: number;
  last_accessed_at: string | null;
  label: string | null;
  created_by: string | null;
  created_at: string;
  share_url: string;
  status: 'active' | 'expired' | 'revoked';
  event_ids: number[] | null;
  event_count: number | null;
  report_name: string | null;
}

interface ShareLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  uploadId: string;
  reportName: string;
  selectedEventIds?: Set<number>;
  events?: HMCEvent[];
  totalEventCount?: number;
}

// Copy icon
const CopyIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
  </svg>
);

// Check icon
const CheckIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

// Link icon
const LinkIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
  </svg>
);

// Trash icon
const TrashIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

export const ShareLinkModal: React.FC<ShareLinkModalProps> = ({
  isOpen,
  onClose,
  uploadId,
  reportName,
  selectedEventIds,
  events,
  totalEventCount,
}) => {
  // Form state
  const [expiresDays, setExpiresDays] = useState(7);
  const [label, setLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Share mode: 'all' or 'selected'
  const [shareMode, setShareMode] = useState<'all' | 'selected'>('all');

  // Existing links
  const [shareLinks, setShareLinks] = useState<ShareLink[]>([]);
  const [loadingLinks, setLoadingLinks] = useState(false);

  // Copy state
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  // Newly created link (to highlight)
  const [newLinkToken, setNewLinkToken] = useState<string | null>(null);

  // Ref for scrolling to existing links section
  const existingLinksRef = useRef<HTMLDivElement>(null);

  // Calculate selected count
  const selectedCount = selectedEventIds?.size || 0;
  const hasSelection = selectedCount > 0;

  // Calculate shareable events (excluding assigned suite events for "Share All" mode)
  const shareableEvents = useMemo(() => {
    if (!events) return [];
    return events.filter((e) => !isAssignedSuiteEvent(e.opponent));
  }, [events]);

  const shareableEventIds = useMemo(() => {
    return shareableEvents.map((e) => e.id);
  }, [shareableEvents]);

  const assignedEventCount = events ? events.length - shareableEvents.length : 0;

  // Fetch existing share links
  const fetchShareLinks = useCallback(async () => {
    if (!uploadId) return;

    setLoadingLinks(true);
    try {
      const response = await apiClient.get<{
        share_links: ShareLink[];
        total: number;
      }>(`/hmc/share-links?upload_id=${encodeURIComponent(uploadId)}`);

      setShareLinks(response.data.share_links);
    } catch (error) {
      console.error('[ShareLinkModal] Failed to fetch share links:', error);
    } finally {
      setLoadingLinks(false);
    }
  }, [uploadId]);

  // Load links when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchShareLinks();
      setLabel(''); // Reset form
      setExpiresDays(7);
      setCreateError(null);
      setNewLinkToken(null);
      // Default to 'selected' mode if events are pre-selected
      setShareMode(hasSelection ? 'selected' : 'all');
    }
  }, [isOpen, fetchShareLinks, hasSelection]);

  // Create new share link
  const handleCreate = async () => {
    if (creating) return;

    // Validate selection mode
    if (shareMode === 'selected' && !hasSelection) {
      setCreateError('No events selected. Please select events or choose "Share All Events".');
      return;
    }

    setCreating(true);
    setCreateError(null);

    try {
      // Build request payload
      const payload: {
        upload_id: string;
        expires_days: number;
        label: string | null;
        event_ids?: number[];
      } = {
        upload_id: uploadId,
        expires_days: expiresDays,
        label: label.trim() || null,
      };

      // Add event_ids based on share mode
      if (shareMode === 'selected' && selectedEventIds) {
        // Selected mode: use selected event IDs (user manually selected)
        payload.event_ids = Array.from(selectedEventIds);
      } else if (shareMode === 'all' && shareableEventIds.length > 0) {
        // All mode: use shareable event IDs (excludes assigned suite events)
        payload.event_ids = shareableEventIds;
      }

      const response = await apiClient.post<ShareLink>('/hmc/share-links', payload);

      // Add to list and highlight
      setShareLinks((prev) => [response.data, ...prev]);
      setNewLinkToken(response.data.share_token);

      // Reset form
      setLabel('');
      setExpiresDays(7);

      // Auto-copy new link
      handleCopy(response.data.share_url, response.data.share_token);

      // Scroll to existing links section to show the new link
      setTimeout(() => {
        existingLinksRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (error: any) {
      console.error('[ShareLinkModal] Failed to create share link:', error);
      setCreateError(error.response?.data?.detail || 'Failed to create share link');
    } finally {
      setCreating(false);
    }
  };

  // Show confirmation dialog before revoking
  const handleRevoke = (link: ShareLink) => {
    setLinkToRevoke(link);
  };

  // Actually revoke the share link after confirmation
  const confirmRevoke = async () => {
    if (!linkToRevoke) return;

    setRevoking(true);
    try {
      await apiClient.delete(`/hmc/share-links/${linkToRevoke.share_token}`);

      // Remove link from list immediately (don't just mark as revoked)
      setShareLinks((prev) => prev.filter((link) => link.share_token !== linkToRevoke.share_token));
      setLinkToRevoke(null);
    } catch (error) {
      console.error('[ShareLinkModal] Failed to revoke share link:', error);
    } finally {
      setRevoking(false);
    }
  };

  // Build confirmation message with views and days remaining
  const getRevokeConfirmationMessage = (link: ShareLink): React.ReactNode => {
    const daysLeft = getDaysUntilExpiration(link.expires_at);
    const viewCount = link.access_count || 0;
    const daysText = daysLeft <= 0 ? 'expired' : daysLeft === 1 ? '1 Day' : `${daysLeft} Days`;

    return (
      <>
        This guest URL has <span className="font-semibold text-white">{viewCount} {viewCount === 1 ? 'View' : 'Views'}</span>
        {daysLeft > 0 && (
          <> and <span className="font-semibold text-white">{daysText}</span> remaining until it expires</>
        )}
        . This action cannot be undone.
      </>
    );
  };

  // Copy to clipboard
  const handleCopy = async (url: string, token: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 2000);
    } catch (error) {
      console.error('[ShareLinkModal] Failed to copy to clipboard:', error);
    }
  };

  // Format date for display
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Calculate days until expiration
  const getDaysUntilExpiration = (expiresAt: string): number => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diffMs = expires.getTime() - now.getTime();
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  };

  // Get expiration countdown badge
  const getExpirationBadge = (link: ShareLink) => {
    if (link.status !== 'active' || link.is_revoked) return null;

    const daysLeft = getDaysUntilExpiration(link.expires_at);

    if (daysLeft <= 0) {
      return <span className="px-2 py-0.5 text-xs bg-red-500/20 text-red-400 rounded-full">Expired</span>;
    }
    if (daysLeft === 1) {
      return <span className="px-2 py-0.5 text-xs bg-amber-500/20 text-amber-400 rounded-full animate-pulse">Expires tomorrow</span>;
    }
    if (daysLeft <= 3) {
      return <span className="px-2 py-0.5 text-xs bg-amber-500/20 text-amber-400 rounded-full">{daysLeft} days left</span>;
    }
    if (daysLeft <= 7) {
      return <span className="px-2 py-0.5 text-xs bg-yellow-500/20 text-yellow-400 rounded-full">{daysLeft} days left</span>;
    }
    return <span className="px-2 py-0.5 text-xs bg-gray-500/20 text-dark-muted rounded-full">{daysLeft} days left</span>;
  };

  // Truncate URL for display
  const truncateUrl = (url: string, maxLength: number = 50): string => {
    if (url.length <= maxLength) return url;
    // Show beginning and end of URL
    const start = url.substring(0, 30);
    const end = url.substring(url.length - 15);
    return `${start}...${end}`;
  };

  // State for usage details modal
  const [usageDetailsLink, setUsageDetailsLink] = useState<ShareLink | null>(null);

  // State for delete confirmation dialog
  const [linkToRevoke, setLinkToRevoke] = useState<ShareLink | null>(null);
  const [revoking, setRevoking] = useState(false);

  // Get status badge
  const getStatusBadge = (link: ShareLink) => {
    if (link.status === 'revoked' || link.is_revoked) {
      return <span className="px-2 py-0.5 text-xs bg-red-500/20 text-red-400 rounded-full">Revoked</span>;
    }
    if (link.status === 'expired') {
      return <span className="px-2 py-0.5 text-xs bg-yellow-500/20 text-yellow-400 rounded-full">Expired</span>;
    }
    return <span className="px-2 py-0.5 text-xs bg-green-500/20 text-green-400 rounded-full">Active</span>;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal content */}
      <div data-testid="share-link-modal" className="relative bg-dark-surface border border-dark-border/30 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-dark-border/30">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-brand-500/20 rounded-lg flex items-center justify-center">
              <LinkIcon />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Share Report</h2>
              <p className="text-sm text-dark-muted truncate max-w-[300px]">{reportName}</p>
            </div>
          </div>
          <button
            data-testid="share-link-close-button"
            onClick={onClose}
            className="p-2 text-dark-muted hover:text-white hover:bg-dark-border rounded-lg transition-all duration-150"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Create new link section */}
          <div className="p-6 border-b border-dark-border/30">
            <h3 className="text-sm font-medium text-white mb-4">Create New Link</h3>

            <div className="space-y-4">
              {/* Share mode selector */}
              <div>
                <label className="block text-sm text-dark-muted mb-2">Events to Share</label>
                <div className="space-y-2">
                  {/* Share All Events */}
                  <label
                    data-testid="share-mode-all"
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all duration-150 ${
                      shareMode === 'all'
                        ? 'bg-brand-500/10 border-brand-500/50'
                        : 'bg-dark-bg border-dark-border/30 hover:border-dark-border/30'
                    }`}
                  >
                    <input
                      type="radio"
                      name="shareMode"
                      value="all"
                      checked={shareMode === 'all'}
                      onChange={() => setShareMode('all')}
                      className="sr-only"
                    />
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      shareMode === 'all' ? 'border-brand-500' : 'border-gray-500'
                    }`}>
                      {shareMode === 'all' && (
                        <div className="w-2 h-2 rounded-full bg-brand-500" />
                      )}
                    </div>
                    <div className="flex-1">
                      <span className="text-white">Share All Events</span>
                      <span className="text-dark-muted ml-2 text-sm">
                        ({shareableEvents.length} events)
                      </span>
                      {assignedEventCount > 0 && (
                        <span className="block text-xs text-amber-400 mt-0.5">
                          {assignedEventCount} assigned suite event{assignedEventCount > 1 ? 's' : ''} excluded
                        </span>
                      )}
                    </div>
                  </label>

                  {/* Share Selected Events */}
                  <label
                    data-testid="share-mode-selected"
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all duration-150 ${
                      !hasSelection ? 'opacity-50 cursor-not-allowed' : ''
                    } ${
                      shareMode === 'selected'
                        ? 'bg-purple-500/10 border-purple-500/50'
                        : 'bg-dark-bg border-dark-border/30 hover:border-dark-border/30'
                    }`}
                  >
                    <input
                      type="radio"
                      name="shareMode"
                      value="selected"
                      checked={shareMode === 'selected'}
                      onChange={() => hasSelection && setShareMode('selected')}
                      disabled={!hasSelection}
                      className="sr-only"
                    />
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      shareMode === 'selected' ? 'border-purple-500' : 'border-gray-500'
                    }`}>
                      {shareMode === 'selected' && (
                        <div className="w-2 h-2 rounded-full bg-purple-500" />
                      )}
                    </div>
                    <div className="flex-1">
                      <span className="text-white">Share Selected Events Only</span>
                      {hasSelection ? (
                        <span className="text-purple-400 ml-2 text-sm">
                          ({selectedCount} selected)
                        </span>
                      ) : (
                        <span className="text-dark-muted ml-2 text-sm">(no events selected)</span>
                      )}
                    </div>
                  </label>
                </div>

                {/* Show selected events preview */}
                {shareMode === 'selected' && hasSelection && events && (
                  <div className="mt-3 p-3 bg-dark-bg rounded-xl border border-dark-border/30 max-h-48 overflow-y-auto">
                    <div className="text-xs text-dark-muted mb-2">Selected events ({selectedCount}):</div>
                    <div className="space-y-1">
                      {events
                        .filter(e => selectedEventIds?.has(e.id))
                        .map(event => (
                          <div key={event.id} className="text-sm text-dark-text truncate">
                            {event.opponent || event.special_event || 'Event'} - {parseLocalDate(event.event_date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) ?? 'TBD'}
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Expiration selector */}
              <div>
                <label className="block text-sm text-dark-muted mb-1.5">Expires in</label>
                <select
                  data-testid="expiration-select"
                  value={expiresDays}
                  onChange={(e) => setExpiresDays(Number(e.target.value))}
                  className="w-full px-3 py-2 bg-dark-bg border border-dark-border/30 rounded-xl text-white focus:outline-none focus:border-brand-500 min-h-[44px]"
                >
                  <option value={1}>1 day</option>
                  <option value={3}>3 days</option>
                  <option value={7}>7 days</option>
                  <option value={14}>14 days</option>
                  <option value={30}>30 days</option>
                </select>
              </div>

              {/* Label input */}
              <div>
                <label className="block text-sm text-dark-muted mb-1.5">Label (optional)</label>
                <input
                  data-testid="link-label-input"
                  type="text"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="e.g., For Sales Team, Client Demo"
                  maxLength={255}
                  className="w-full px-3 py-2 bg-dark-bg border border-dark-border/30 rounded-xl text-white placeholder-dark-muted focus:outline-none focus:border-brand-500 min-h-[44px]"
                />
              </div>

              {/* Error message */}
              {createError && (
                <p className="text-sm text-red-400">{createError}</p>
              )}

              {/* Create button */}
              <button
                data-testid="generate-share-link-button"
                onClick={handleCreate}
                disabled={creating || (shareMode === 'selected' && !hasSelection)}
                className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all duration-150 min-h-[44px] ${
                  creating || (shareMode === 'selected' && !hasSelection)
                    ? 'bg-brand-500/50 text-white/70 cursor-not-allowed'
                    : 'bg-brand-500 text-white hover:bg-brand-600'
                }`}
              >
                {creating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <LinkIcon />
                    Generate Share Link
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Existing links section */}
          <div data-testid="existing-links-section" className="p-6" ref={existingLinksRef}>
            <h3 className="text-sm font-medium text-white mb-4">
              Existing Links {shareLinks.length > 0 && `(${shareLinks.length})`}
            </h3>

            {loadingLinks ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500"></div>
              </div>
            ) : shareLinks.length === 0 ? (
              <p className="text-center text-dark-muted py-8">
                No share links created yet
              </p>
            ) : (
              <div className="space-y-3">
                {shareLinks.map((link) => {
                  const isNew = link.share_token === newLinkToken;
                  const isActive = link.status === 'active' && !link.is_revoked;

                  return (
                    <div
                      key={link.id}
                      data-testid={`share-link-item-${link.id}`}
                      className={`p-4 rounded-lg border transition-all duration-150 ${
                        isNew
                          ? 'bg-brand-500/10 border-brand-500/30'
                          : 'bg-dark-bg border-dark-border/30'
                      }`}
                    >
                      {/* Link header */}
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            {link.label ? (
                              <span className="font-medium text-white">{link.label}</span>
                            ) : (
                              <span className="text-dark-muted italic">No label</span>
                            )}
                            {getStatusBadge(link)}
                            {isNew && (
                              <span className="px-2 py-0.5 text-xs bg-brand-500/20 text-brand-400 rounded-full">
                                New
                              </span>
                            )}
                            {link.event_ids && link.event_count && (
                              <span className="px-2 py-0.5 text-xs bg-purple-500/20 text-purple-400 rounded-full">
                                {link.event_count} event{link.event_count !== 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                          {/* Report name - shows which file this link is attached to */}
                          {link.report_name && (
                            <div className="text-xs text-dark-muted mt-1 truncate" title={link.report_name}>
                              Report: {link.report_name}
                            </div>
                          )}
                        </div>

                        {/* Actions - only revoke button, copy is in URL display */}
                        <div className="flex items-center gap-2">
                          {isActive && (
                            <button
                              data-testid={`revoke-link-${link.id}`}
                              onClick={() => handleRevoke(link)}
                              className="p-2 text-dark-muted hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all duration-150 min-h-[44px] min-w-[44px] flex items-center justify-center"
                              title="Revoke link"
                            >
                              <TrashIcon />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Link URL - compact one-line display with copy button */}
                      {isActive && (
                        <div className="mb-3">
                          <div className={`flex items-center gap-2 p-2 rounded-lg border transition-all duration-150 ${
                            isNew
                              ? 'bg-brand-500/5 border-brand-500/40'
                              : 'bg-dark-surface border-dark-border/30'
                          }`}>
                            {/* URL - truncated to one line */}
                            <div
                              className="flex-1 min-w-0 cursor-pointer"
                              onClick={() => handleCopy(link.share_url, link.share_token)}
                              title={link.share_url}
                            >
                              <div className="text-sm text-white font-mono truncate">
                                {truncateUrl(link.share_url)}
                              </div>
                            </div>

                            {/* Copy button - compact */}
                            <button
                              data-testid={`copy-link-${link.id}`}
                              onClick={() => handleCopy(link.share_url, link.share_token)}
                              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg font-medium text-xs transition-all min-h-[36px] shrink-0 ${
                                copiedToken === link.share_token
                                  ? 'bg-green-500 text-white'
                                  : 'bg-brand-500 text-white hover:bg-brand-600 active:scale-95'
                              }`}
                              title="Copy to clipboard"
                            >
                              {copiedToken === link.share_token ? (
                                <>
                                  <CheckIcon />
                                  <span>Copied!</span>
                                </>
                              ) : (
                                <>
                                  <CopyIcon />
                                  <span>Copy</span>
                                </>
                              )}
                            </button>
                          </div>
                          {isNew && (
                            <p className="text-xs text-brand-400 mt-1">Link auto-copied to clipboard!</p>
                          )}
                        </div>
                      )}

                      {/* Enhanced Stats Section */}
                      <div className="bg-dark-bg/50 rounded-lg p-3 border border-dark-border/30">
                        {/* Primary stats row */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            {/* Views count - prominent */}
                            <div className="flex items-center gap-1.5">
                              <svg className="w-4 h-4 text-dark-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              <span className="text-lg font-semibold text-white">{link.access_count}</span>
                              <span className="text-sm text-dark-muted">view{link.access_count !== 1 ? 's' : ''}</span>
                            </div>

                            {/* Expiration countdown badge */}
                            {getExpirationBadge(link)}
                          </div>

                          {/* See Usage button */}
                          <button
                            onClick={() => setUsageDetailsLink(link)}
                            className="px-3 py-1.5 text-xs font-medium text-brand-400 hover:text-brand-300 hover:bg-brand-500/10 rounded-lg transition-all duration-150"
                          >
                            See Usage →
                          </button>
                        </div>

                        {/* Secondary stats row */}
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-dark-muted">
                          <span className="flex items-center gap-1">
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            Created {formatDate(link.created_at)}
                          </span>
                          <span className="flex items-center gap-1">
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Expires {formatDate(link.expires_at)}
                          </span>
                          {link.last_accessed_at && (
                            <span className="flex items-center gap-1">
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Last viewed {formatDate(link.last_accessed_at)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Usage Details Modal */}
      {usageDetailsLink && (
        <UsageDetailsModal
          link={usageDetailsLink}
          events={events}
          onClose={() => setUsageDetailsLink(null)}
          formatDate={formatDate}
          getDaysUntilExpiration={getDaysUntilExpiration}
        />
      )}

      {/* Delete confirmation dialog */}
      <ConfirmationDialog
        isOpen={!!linkToRevoke}
        onClose={() => setLinkToRevoke(null)}
        onConfirm={confirmRevoke}
        title="Are you sure you want to Delete?"
        message={linkToRevoke ? getRevokeConfirmationMessage(linkToRevoke) : ''}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={revoking}
      />
    </div>
  );
};

// Usage Details Modal Component
interface UsageDetailsModalProps {
  link: ShareLink;
  events?: HMCEvent[];
  onClose: () => void;
  formatDate: (dateStr: string) => string;
  getDaysUntilExpiration: (expiresAt: string) => number;
}

const UsageDetailsModal: React.FC<UsageDetailsModalProps> = ({
  link,
  events,
  onClose,
  formatDate,
  getDaysUntilExpiration,
}) => {
  const [activeTab, setActiveTab] = useState<'summary' | 'events' | 'settings'>('summary');

  // Get events included in this link
  const includedEvents = useMemo(() => {
    if (!events || !link.event_ids) return [];
    return events.filter((e) => link.event_ids!.includes(e.id));
  }, [events, link.event_ids]);

  const daysLeft = getDaysUntilExpiration(link.expires_at);

  const tabs = [
    { id: 'summary' as const, label: 'Summary', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
    { id: 'events' as const, label: 'Events', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
    { id: 'settings' as const, label: 'Settings', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' },
  ];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-dark-surface border border-dark-border/30 rounded-xl w-full max-w-lg max-h-[80vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-dark-border/30">
          <div>
            <h3 className="text-lg font-semibold text-white">Link Usage Details</h3>
            {link.label && (
              <p className="text-sm text-dark-muted">{link.label}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 text-dark-muted hover:text-white hover:bg-dark-border rounded-lg transition-all duration-150"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-dark-border/30">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-all duration-150 ${
                activeTab === tab.id
                  ? 'text-brand-400 border-b-2 border-brand-500 bg-brand-500/5'
                  : 'text-dark-muted hover:text-white hover:bg-dark-border/50'
              }`}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
              </svg>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="p-5 overflow-y-auto max-h-[50vh]">
          {/* Summary Tab */}
          {activeTab === 'summary' && (
            <div className="space-y-4">
              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-dark-bg rounded-xl p-4 border border-dark-border/30">
                  <div className="flex items-center gap-2 text-dark-muted text-xs mb-1">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    Total Views
                  </div>
                  <div className="text-2xl font-bold text-white">{link.access_count}</div>
                </div>

                <div className="bg-dark-bg rounded-xl p-4 border border-dark-border/30">
                  <div className="flex items-center gap-2 text-dark-muted text-xs mb-1">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Time Left
                  </div>
                  <div className={`text-2xl font-bold ${
                    daysLeft <= 1 ? 'text-red-400' : daysLeft <= 3 ? 'text-amber-400' : 'text-white'
                  }`}>
                    {daysLeft <= 0 ? 'Expired' : `${daysLeft}d`}
                  </div>
                </div>

                <div className="bg-dark-bg rounded-xl p-4 border border-dark-border/30">
                  <div className="flex items-center gap-2 text-dark-muted text-xs mb-1">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Events Included
                  </div>
                  <div className="text-2xl font-bold text-white">{link.event_count || includedEvents.length}</div>
                </div>

                <div className="bg-dark-bg rounded-xl p-4 border border-dark-border/30">
                  <div className="flex items-center gap-2 text-dark-muted text-xs mb-1">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Last Viewed
                  </div>
                  <div className="text-lg font-bold text-white">
                    {link.last_accessed_at ? formatDate(link.last_accessed_at) : 'Never'}
                  </div>
                </div>
              </div>

              {/* Timeline */}
              <div className="bg-dark-bg rounded-xl p-4 border border-dark-border/30">
                <h4 className="text-sm font-medium text-white mb-3">Timeline</h4>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                    <span className="text-sm text-dark-muted">Created</span>
                    <span className="text-sm text-white ml-auto">{formatDate(link.created_at)}</span>
                  </div>
                  {link.last_accessed_at && (
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <span className="text-sm text-dark-muted">Last accessed</span>
                      <span className="text-sm text-white ml-auto">{formatDate(link.last_accessed_at)}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${daysLeft <= 0 ? 'bg-red-500' : 'bg-amber-500'}`}></div>
                    <span className="text-sm text-dark-muted">Expires</span>
                    <span className={`text-sm ml-auto ${daysLeft <= 1 ? 'text-red-400' : 'text-white'}`}>
                      {formatDate(link.expires_at)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Events Tab */}
          {activeTab === 'events' && (
            <div className="space-y-3">
              {includedEvents.length === 0 ? (
                <div className="text-center py-8 text-dark-muted">
                  <p>No event data available</p>
                  <p className="text-xs mt-1">Event details may have been updated since link creation</p>
                </div>
              ) : (
                <>
                  <div className="text-xs text-dark-muted mb-2">
                    {includedEvents.length} event{includedEvents.length !== 1 ? 's' : ''} included in this share link
                  </div>
                  {includedEvents.map((event) => {
                    const eventDate = parseLocalDate(event.event_date);
                    return (
                      <div
                        key={event.id}
                        className="flex items-center gap-3 p-3 bg-dark-bg rounded-xl border border-dark-border/30"
                      >
                        <div className="w-10 h-10 bg-brand-500/20 rounded-lg flex items-center justify-center text-brand-400 text-xs font-bold">
                          {eventDate?.getDate() ?? '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white truncate">
                            {event.opponent || event.special_event || 'Event'}
                          </p>
                          <p className="text-xs text-dark-muted">
                            {eventDate?.toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                            }) ?? 'Date TBD'}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-4">
              <div className="bg-dark-bg rounded-xl p-4 border border-dark-border/30">
                <h4 className="text-sm font-medium text-white mb-3">Link Configuration</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-dark-muted">Label</span>
                    <span className="text-sm text-white">{link.label || 'None'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-dark-muted">Status</span>
                    <span className={`text-sm ${
                      link.is_revoked ? 'text-red-400' :
                      link.status === 'expired' ? 'text-yellow-400' : 'text-green-400'
                    }`}>
                      {link.is_revoked ? 'Revoked' : link.status === 'expired' ? 'Expired' : 'Active'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-dark-muted">Created by</span>
                    <span className="text-sm text-white">{link.created_by || 'Unknown'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-dark-muted">Share token</span>
                    <span className="text-xs text-dark-muted font-mono">{link.share_token.substring(0, 8)}...</span>
                  </div>
                </div>
              </div>

              <div className="bg-dark-bg rounded-xl p-4 border border-dark-border/30">
                <h4 className="text-sm font-medium text-white mb-3">Visibility Settings</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-dark-muted">Events shared</span>
                    <span className="text-sm text-white">
                      {link.event_ids ? `${link.event_count || link.event_ids.length} selected` : 'All events'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-dark-muted">Requires login</span>
                    <span className="text-sm text-green-400">No (Guest access)</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareLinkModal;
